import type {
  Cohort,
  MCQConfig,
  NPSConfig,
  PolarConfig,
  LikertConfig,
  Question,
  Response,
} from "./types";

/** Minimum number of responses in a cohort before we cluster or display its panel. */
export const MIN_COHORT_SIZE = 5;

/**
 * Map a single response onto its cohort key, given the question it belongs to.
 * Returns null for cohorts we don't track in v1 (e.g. MCQ option cohorts when
 * why_for_all is off) or when the typed fields are missing.
 */
export function cohortForResponse(
  question: Question,
  response: Response,
): Cohort | null {
  switch (question.question_type) {
    case "open_text":
      return null;
    case "nps": {
      const r = response.rating;
      if (r == null) return null;
      if (r <= 6) return "detractor";
      if (r <= 8) return "passive";
      return "promoter";
    }
    case "multiple_choice": {
      const cfg = question.config as MCQConfig;
      if (response.is_other === true) return "other";
      if (cfg.why_for_all && response.choice_key) return response.choice_key;
      return null;
    }
    case "polar": {
      const cfg = question.config as PolarConfig;
      if (response.choice_key === cfg.side_a.key) return "side_a";
      if (response.choice_key === cfg.side_b.key) return "side_b";
      return null;
    }
    case "likert": {
      const r = response.rating;
      if (r == null) return null;
      if (r <= 2) return "low";
      if (r === 3) return "mid";
      return "high";
    }
  }
}

/** Group responses by their cohort key. Responses with cohort=null are skipped. */
export function groupByCohort(
  question: Question,
  responses: Response[],
): Record<string, Response[]> {
  const out: Record<string, Response[]> = {};
  for (const r of responses) {
    const c = cohortForResponse(question, r);
    if (!c) continue;
    (out[c] ??= []).push(r);
  }
  return out;
}

/**
 * NPS score = %promoters − %detractors (range −100..+100).
 * Returns 0 for an empty input rather than NaN so the UI doesn't have to guard.
 */
export function computeNpsScore(responses: Response[]): number {
  const rated = responses.filter((r) => r.rating != null);
  if (rated.length === 0) return 0;
  const detractors = rated.filter((r) => (r.rating ?? 0) <= 6).length;
  const promoters = rated.filter((r) => (r.rating ?? 0) >= 9).length;
  return Math.round(((promoters - detractors) / rated.length) * 100);
}

/** MCQ distribution: one row per configured option plus a synthetic 'other' row. */
export function computeMcqDistribution(
  question: Question,
  responses: Response[],
): Array<{ key: string; label: string; count: number; is_other: boolean }> {
  const cfg = question.config as MCQConfig;
  const counts = new Map<string, number>();
  let otherCount = 0;
  for (const r of responses) {
    if (r.is_other === true) { otherCount += 1; continue; }
    if (!r.choice_key) continue;
    counts.set(r.choice_key, (counts.get(r.choice_key) ?? 0) + 1);
  }
  const rows = cfg.options.map((opt) => ({
    key: opt.key,
    label: opt.label,
    count: counts.get(opt.key) ?? 0,
    is_other: false,
  }));
  if (cfg.allow_other) {
    rows.push({ key: "__other__", label: cfg.other_label, count: otherCount, is_other: true });
  }
  return rows;
}

/** Polar split: counts and percentages for side A and side B. */
export function computePolarSplit(
  question: Question,
  responses: Response[],
): { a_count: number; b_count: number; a_pct: number; b_pct: number } {
  const cfg = question.config as PolarConfig;
  let a = 0;
  let b = 0;
  for (const r of responses) {
    if (r.choice_key === cfg.side_a.key) a += 1;
    else if (r.choice_key === cfg.side_b.key) b += 1;
  }
  const total = a + b;
  if (total === 0) return { a_count: 0, b_count: 0, a_pct: 0, b_pct: 0 };
  return {
    a_count: a,
    b_count: b,
    a_pct: Math.round((a / total) * 100),
    b_pct: Math.round((b / total) * 100),
  };
}

/** NPS scale labels for stage display ("Detraktor"/"Passiv"/"Promoter"). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function npsScaleLabels(_config: NPSConfig): Record<Cohort, string> {
  return {
    detractor: "Detraktoren",
    passive: "Passiv",
    promoter: "Promotoren",
  } as Record<Cohort, string>;
}

/** Likert distribution: count per scale value plus mean, for stage/insights bars. */
export function computeLikertDistribution(
  question: Question,
  responses: Response[],
): { counts: Array<{ value: number; count: number }>; mean: number; total: number } {
  const cfg = question.config as LikertConfig;
  const min = cfg.scale?.min ?? 1;
  const max = cfg.scale?.max ?? 5;
  const rated = responses.filter(
    (r) => r.rating != null && r.rating >= min && r.rating <= max,
  );
  const counts: Array<{ value: number; count: number }> = [];
  for (let v = min; v <= max; v++) {
    counts.push({ value: v, count: rated.filter((r) => r.rating === v).length });
  }
  const total = rated.length;
  const mean =
    total === 0 ? 0 : rated.reduce((s, r) => s + (r.rating ?? 0), 0) / total;
  return { counts, mean, total };
}
